import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

import { expandScene, type ExpandedScene } from '../../lib/prefab.js';
import type { EditorCommand } from '../../lib/scene-doc.js';
import { usePrefabs } from '../../state/prefabs.js';
import { useScene } from '../../state/scene.js';
import { ContextMenu, type MenuItem } from '../ui/controls.js';
import '../play/play-tools.css';

/**
 * Where the Inspector's component cards send their edits, and how fields are
 * marked. The default target is the open scene; additive scenes, the running
 * game (Play mode live Inspector) and prefab instances provide their own.
 */
export interface InspectorTarget {
  apply: (command: EditorCommand) => void;
  /** Marks a property (`transform.position`, `components.light.intensity`, `components.light`). */
  mark?: (id: string, path: string) => FieldMark | null;
  /** Right-click menu of a property (Apply / Revert override, Keep…). */
  fieldMenu?: (id: string, path: string) => MenuItem[];
  /** Extra entries of a component card's menu. */
  componentMenu?: (id: string, key: string) => MenuItem[];
}

export type FieldMark = 'overridden' | 'inherited' | 'kept' | 'edited';

export const InspectorContext = createContext<InspectorTarget | null>(null);

export function InspectorProvider({ target, children }: { target: InspectorTarget; children: ReactNode }) {
  return <InspectorContext.Provider value={target}>{children}</InspectorContext.Provider>;
}

/** The edit target of the surrounding Inspector (the open scene by default). */
export function useInspectorTarget(): InspectorTarget {
  const ctx = useContext(InspectorContext);
  const apply = useScene((s) => s.apply);
  return ctx ?? { apply: (c) => void apply(c) };
}

/** A property row wrapper: override bar / bold label and its right-click menu. */
export function MarkedField({ id, path, children }: { id: string; path: string; children: ReactNode }) {
  const target = useContext(InspectorContext);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const mark = target?.mark?.(id, path) ?? null;
  const items = menu ? (target?.fieldMenu?.(id, path) ?? []) : [];
  if (!target?.mark && !target?.fieldMenu) return <>{children}</>;
  return (
    <div
      className={`marked-field${mark ? ` is-${mark}` : ''}`}
      title={mark === 'overridden' ? 'Overridden in this instance (right-click to apply or revert)' : mark === 'kept' ? 'Kept when play stops' : undefined}
      onContextMenu={(e) => {
        if (!target?.fieldMenu?.(id, path).length) return;
        e.preventDefault();
        setMenu({ x: e.clientX, y: e.clientY });
      }}
    >
      {children}
      {menu && items.length > 0 && <ContextMenu x={menu.x} y={menu.y} items={items} onClose={() => setMenu(null)} />}
    </div>
  );
}

/** The open scene with prefab instances expanded (memoised on the scene and the prefab cache). */
export function useExpandedScene(): ExpandedScene | null {
  const doc = useScene((s) => s.doc);
  const version = usePrefabs((s) => s.version);
  return useMemo(() => {
    void version;
    return doc ? expandScene(doc, usePrefabs.getState().loader()) : null;
  }, [doc, version]);
}
